// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useState, useCallback, MouseEvent } from 'react'
import {
  Button,
  FileUpload,
  Flex,
  Typography,
  Modal,
  UseShowHide,
} from '@teamnine/pcj-ui-v2'
import { useNavigate } from 'react-router-dom'
import { UploadFile } from '@/utils/material-icons'

import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'
import { useUploadTemplate } from '@/app/Templates/hooks/useUploadTemplate'

type UploadTemplateModalProps = {
  modal: UseShowHide
}

export default function UploadTemplateModal({
  modal,
}: UploadTemplateModalProps) {
  const navigate = useNavigate()
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()

  const [files, setFiles] = useState<File[]>([])
  const { mutateAsync: uploadTemplate, isLoading } = useUploadTemplate()

  const onDrop: Parameters<typeof FileUpload>[0]['onDrop'] = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) setFiles([acceptedFiles[0]])
    },
    []
  )

  const onRemove = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleUpload = async () => {
    if (files.length === 0) return
    try {
      const templateId = await uploadTemplate(files[0])
      displayGlobalMessage({
        heading: 'Template successfully uploaded.',
        type: 'success',
      })
      setFiles([])
      modal.hide()
      navigate(`/templates/${templateId}`)
    } catch (err) {
      basicErrorHandler(err)
    }
  }

  return (
    <Modal open={modal.visible} onOpenChange={modal.toggle}>
      <Modal.Trigger asChild>
        <Button aria-label="Upload a template" icon={<UploadFile />}>
          Upload Template
        </Button>
      </Modal.Trigger>
      <Modal.Display aria-label="Upload a template">
        <Modal.Header title="Upload a Template" />
        <Modal.Content>
          <FileUpload
            multiple={false}
            maxFiles={1}
            isLocked={files.length > 0}
            onDrop={onDrop}
            accept={{ 'application/json': ['.json'] }}
          >
            {files.length === 0 ? (
              <Flex align="center" direction="column" gap={8}>
                <Typography variant="16-medium">
                  Select a valid JSON template file, or click to browse
                </Typography>
                <Typography variant="14-regular" color="gray">
                  Max file size: 1MB
                </Typography>
              </Flex>
            ) : (
              files.map((file, index) => (
                <Flex key={index} align="center" gap={16}>
                  <Typography variant="16-medium">{file.name}</Typography>
                  <Button
                    variant="blank"
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation()
                      onRemove(index)
                    }}
                  >
                    Remove
                  </Button>
                </Flex>
              ))
            )}
          </FileUpload>
        </Modal.Content>
        <Modal.Footer
          footerAction={{
            primaryButton: {
              label: 'Upload',
              onClick: handleUpload,
              type: 'button',
              loading: isLoading,
              disabled: files.length === 0,
            },
            secondaryButton: {
              label: 'Cancel',
              closeModal: true,
              onClick: () => setFiles([]),
            },
          }}
        />
      </Modal.Display>
    </Modal>
  )
}
